import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Wallet, Heart, CheckSquare, Plus, Edit3, Trash2, X } from '../icons';
import { useAuth } from '../context/AuthContext';
import { getWeddings, getChecklist, getGuests, getBudget, createWedding, updateWedding, deleteWedding, getBookmarks, updateWeddingCurrency } from '../services/api';
import LaganCalendar from '../components/LaganCalendar';
import { vendorImage } from '../utils/categoryImages';
import { useLanguage } from '../context/LanguageContext';

function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const [wedding, setWedding] = useState(null);
  const [stats, setStats] = useState({ checklist: 0, checklistDone: 0, guests: 0, guestsAccepted: 0, budgetEstimated: 0, budgetActual: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    partner1_name: '', partner2_name: '', wedding_date: '', venue_location: '', city: 'Kathmandu',
    district: '', province: '', guest_count: '', budget_npr: '', wedding_style: 'traditional', story: ''
  });
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [dashMessage, setDashMessage] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  // A failed load used to fall through to the "Create your wedding" form, inviting a duplicate
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    // Helpers (non-delegates) should only access checklist
    if (user.role === 'helper' && user.collaborator?.collaborator_role !== 'delegate') {
      navigate('/checklist');
      return;
    }
    
    getWeddings()
      .then(async (res) => {
        if (res.data.length > 0) {
          const w = res.data[0];
          setWedding(w);
          const [checklistRes, guestsRes, budgetRes] = await Promise.all([
            getChecklist(w.id).catch(() => ({ data: [] })),
            getGuests(w.id).catch(() => ({ data: { guests: [], stats: {} } })),
            getBudget(w.id).catch(() => ({ data: { items: [], summary: {} } })),
          ]);

          setStats({
            checklist: checklistRes.data.length,
            checklistDone: checklistRes.data.filter(i => i.is_completed).length,
            guests: parseInt(guestsRes.data.stats?.total || 0),
            guestsAccepted: parseInt(guestsRes.data.stats?.accepted || 0),
            budgetEstimated: parseFloat(budgetRes.data.summary?.total_estimated || 0),
            budgetActual: parseFloat(budgetRes.data.summary?.total_actual || 0),
          });
        } else if (user?.role === 'couple') {
          // Pre-fill partner1 name for Google-signed-up couples
          setCreateForm(f => ({ ...f, partner1_name: user.first_name || '' }));
          setShowCreateForm(true);
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));

    // Load bookmarks
    getBookmarks().then(res => setBookmarks(res.data || [])).catch(() => {});
  }, [user, navigate, authLoading, reloadKey]);

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  if (loadError) {
    return (
      <div className="dashboard-page">
        <div className="container">
          <div className="empty-state-sm">
            <h2>{t('dashboard.failedToLoad')}</h2>
            <button className="btn btn-primary" style={{ marginTop: 16 }}
              onClick={() => { setLoadError(false); setLoading(true); setReloadKey(k => k + 1); }}>
              {t('coupleTools.tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const daysUntilWedding = wedding?.wedding_date
    ? Math.ceil((new Date(wedding.wedding_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const isOwner = user?.role === 'couple';
  const isDelegate = user?.role === 'helper' && user?.collaborator?.collaborator_role === 'delegate';
  const canWrite = isOwner || (isDelegate && user?.collaborator?.access_level === 'write');

  // Time-based greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('dashboard.goodMorning') : hour < 17 ? t('dashboard.goodAfternoon') : t('dashboard.goodEvening');

  const handleEditDetails = () => {
    setEditForm({
      partner1_name: wedding.partner1_name || '',
      partner2_name: wedding.partner2_name || '',
      wedding_date: wedding.wedding_date ? wedding.wedding_date.split('T')[0] : '',
      venue_location: wedding.venue_location || '',
      city: wedding.city || '',
      district: wedding.district || '',
      province: wedding.province || '',
      guest_count: wedding.guest_count || '',
      budget_npr: wedding.budget_npr || '',
      budget_currency: wedding.budget_currency || 'NPR',
      wedding_style: wedding.wedding_style || 'traditional',
      story: wedding.story || '',
      status: wedding.status || 'planning'
    });
    setEditingDetails(true);
  };

  const handleSaveDetails = async () => {
    setEditSaving(true);
    try {
      const res = await updateWedding(wedding.id, {
        ...editForm,
        guest_count: parseInt(editForm.guest_count) || 0,
        budget_npr: parseFloat(editForm.budget_npr) || 0
      });
      if (editForm.budget_currency && editForm.budget_currency !== wedding.budget_currency) {
        await updateWeddingCurrency(wedding.id, editForm.budget_currency);
      }
      setWedding(res.data);
      setEditingDetails(false);
      setDashMessage({ type: 'success', text: t('dashboard.detailsUpdated') });
      setTimeout(() => setDashMessage(null), 3000);
    } catch (err) {
      setDashMessage({ type: 'error', text: err.response?.data?.error || t('coupleTools.actionFailed') });
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteWedding = async () => {
    try {
      await deleteWedding(wedding.id);
      setWedding(null);
      setDeleteConfirm(false);
      setDashMessage({ type: 'success', text: t('dashboard.weddingDeleted') });
    } catch (err) {
      setDashMessage({ type: 'error', text: err.response?.data?.error || t('coupleTools.actionFailed') });
    }
  };

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>{greeting}, {user?.first_name}! <Heart size={24} className="heart-icon" /></h1>
            {wedding ? (
              <p className="wedding-names">{wedding.partner1_name} & {wedding.partner2_name}</p>
            ) : (
              <p>{t('dashboard.startPlanning')}</p>
            )}
          </div>
          {!wedding && isOwner && !showCreateForm && (
            <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
              <Plus size={16} /> {t('dashboard.createWedding')}
            </button>
          )}
        </div>

        {/* Outside the wedding block so "wedding deleted" is still visible after the wedding is gone */}
        {dashMessage && (
          <div className={`alert alert-${dashMessage.type}`} style={{ marginBottom: '16px' }}>
            {dashMessage.text}
            <button className="alert-close" onClick={() => setDashMessage(null)}>&times;</button>
          </div>
        )}

        {/* Create Wedding Form - auto-show for couples without a wedding */}
        {!wedding && (showCreateForm || isOwner) && isOwner && (
          <div className="detail-card" style={{ marginBottom: '24px' }}>
            <h3>{t('dashboard.createYourWedding')}</h3>
            {createError && <div className="auth-error">{createError}</div>}
            <form onSubmit={async (e) => {
              e.preventDefault();
              setCreateError('');
              if (!createForm.partner1_name || !createForm.partner2_name) {
                setCreateError(t('dashboard.bothNamesRequired'));
                return;
              }
              setCreating(true);
              try {
                const res = await createWedding({
                  ...createForm,
                  guest_count: parseInt(createForm.guest_count) || 0,
                  budget_npr: parseFloat(createForm.budget_npr) || 0
                });
                setWedding(res.data);
                setShowCreateForm(false);
              } catch (err) {
                setCreateError(err.response?.data?.error || t('coupleTools.actionFailed'));
              } finally {
                setCreating(false);
              }
            }}>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('dashboard.partner1Name')} *</label>
                  <input type="text" placeholder="e.g. Sita" value={createForm.partner1_name} onChange={(e) => setCreateForm({ ...createForm, partner1_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>{t('dashboard.partner2Name')} *</label>
                  <input type="text" placeholder="e.g. Ram" value={createForm.partner2_name} onChange={(e) => setCreateForm({ ...createForm, partner2_name: e.target.value })} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('dashboard.weddingDate')}</label>
                  <input type="date" value={createForm.wedding_date} onChange={(e) => setCreateForm({ ...createForm, wedding_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t('dashboard.venueLocation')}</label>
                  <input type="text" placeholder="e.g. Soaltee Hotel" value={createForm.venue_location} onChange={(e) => setCreateForm({ ...createForm, venue_location: e.target.value })} />
                </div>
              </div>
              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>{t('dashboard.city')}</label>
                  <input type="text" placeholder="Kathmandu" value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t('dashboard.district')}</label>
                  <input type="text" placeholder="Kathmandu" value={createForm.district} onChange={(e) => setCreateForm({ ...createForm, district: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t('dashboard.province')}</label>
                  <input type="text" placeholder="Bagmati" value={createForm.province} onChange={(e) => setCreateForm({ ...createForm, province: e.target.value })} />
                </div>
              </div>
              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>{t('dashboard.expectedGuests')}</label>
                  <input type="number" placeholder="200" value={createForm.guest_count} onChange={(e) => setCreateForm({ ...createForm, guest_count: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t('dashboard.budgetNPR')}</label>
                  <input type="number" placeholder="500000" value={createForm.budget_npr} onChange={(e) => setCreateForm({ ...createForm, budget_npr: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t('dashboard.weddingStyle')}</label>
                  <select value={createForm.wedding_style} onChange={(e) => setCreateForm({ ...createForm, wedding_style: e.target.value })}>
                    <option value="traditional">{t('dashboard.traditional')}</option>
                    <option value="modern">{t('dashboard.modern')}</option>
                    <option value="fusion">{t('dashboard.fusion')}</option>
                    <option value="destination">{t('dashboard.destination')}</option>
                    <option value="intimate">{t('dashboard.intimate')}</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>{t('dashboard.loveStory')}</label>
                <textarea placeholder={t('dashboard.loveStoryPlaceholder')} value={createForm.story} onChange={(e) => setCreateForm({ ...createForm, story: e.target.value })} rows={3} />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? t('dashboard.creating') : t('dashboard.createWedding')}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setShowCreateForm(false)}>{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        )}

        {wedding && (
          <>
            {/* Countdown row. `daysUntilWedding && ...` used to print a stray "0" on the
                wedding day and showed nothing at all with no date or a past date. */}
            <div className="dashboard-top-row">
              <div style={{ flex: 1 }}>
                {daysUntilWedding === null ? (
                  <div className="detail-card" style={{ textAlign: 'center' }}>
                    <Calendar size={28} style={{ color: 'var(--primary)' }} />
                    <p style={{ margin: '8px 0 12px' }}>{isOwner ? t('dashboard.noDateOwner') : t('dashboard.noDateHelper')}</p>
                    {isOwner && !editingDetails && (
                      <button className="btn btn-primary" onClick={handleEditDetails}>{t('dashboard.setDate')}</button>
                    )}
                  </div>
                ) : (
                  <div className="countdown-banner">
                    {daysUntilWedding > 0 ? (
                      <>
                        <div className="countdown-number">{daysUntilWedding}</div>
                        <div className="countdown-label">{t('dashboard.daysUntilWedding')}</div>
                      </>
                    ) : (
                      <div className="countdown-label" style={{ fontSize: 24, fontWeight: 700 }}>
                        {daysUntilWedding === 0 ? t('dashboard.weddingToday') : t('dashboard.weddingPast')}
                      </div>
                    )}
                    <div className="countdown-date">
                      <Calendar size={16} />
                      {new Date(wedding.wedding_date).toLocaleDateString(lang === 'ne' ? 'ne-NP' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* The review-request email was the only way to reach /write-review */}
            {isOwner && wedding.review_enabled && (
              <div className="detail-card" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <p style={{ margin: 0 }}>{t('dashboard.reviewPrompt')}</p>
                <Link to="/write-review" className="btn btn-primary">{t('dashboard.writeReviews')}</Link>
              </div>
            )}

            {/* Lagan Calendar — horizontal strip below countdown */}
            <div className="dashboard-lagan-row">
              <div className="lagan-strip-inner">
                <div className="lagan-strip-info">
                  <h3 className="lagan-strip-title">
                    <Calendar size={18} style={{ color: '#DC143C', verticalAlign: 'middle', marginRight: 6 }} />
                    {lang === 'ne' ? 'विवाह लगन पात्रो' : t('dashboard.laganCalendar')}
                  </h3>
                  <p className="lagan-strip-sub">{lang === 'ne' ? 'शुभ विवाह मुहूर्त — नेपाली पात्रो अनुसार' : 'Auspicious wedding dates per the Nepali BS calendar'}</p>
                </div>
                <LaganCalendar lang={lang} />
              </div>
            </div>

            {/* Quick Stats */}
            <div className="dashboard-stats">
              <Link to="/checklist" className="stat-card">
                <CheckSquare size={28} className="stat-icon green" />
                <div className="stat-info">
                  <span className="stat-value">{stats.checklistDone}/{stats.checklist}</span>
                  <span className="stat-label">{t('dashboard.checklistTasks')}</span>
                </div>
                <div className="stat-progress">
                  <div className="progress-bar" style={{ width: `${stats.checklist ? (stats.checklistDone / stats.checklist) * 100 : 0}%` }}></div>
                </div>
              </Link>

              <Link to="/guests" className="stat-card">
                <Users size={28} className="stat-icon blue" />
                <div className="stat-info">
                  <span className="stat-value">{stats.guestsAccepted}/{stats.guests}</span>
                  <span className="stat-label">{t('dashboard.guestsAccepted')}</span>
                </div>
                <div className="stat-progress">
                  <div className="progress-bar blue" style={{ width: `${stats.guests ? (stats.guestsAccepted / stats.guests) * 100 : 0}%` }}></div>
                </div>
              </Link>

              <Link to="/budget" className="stat-card">
                <Wallet size={28} className="stat-icon orange" />
                <div className="stat-info">
                  <span className="stat-value">{t('common.NPR')} {stats.budgetActual.toLocaleString()}</span>
                  <span className="stat-label">{t('dashboard.ofNPR')} {stats.budgetEstimated.toLocaleString()} {t('dashboard.spent')}</span>
                </div>
                <div className="stat-progress">
                  <div className="progress-bar orange" style={{ width: `${stats.budgetEstimated ? (stats.budgetActual / stats.budgetEstimated) * 100 : 0}%` }}></div>
                </div>
              </Link>
            </div>

            {/* Wedding Details */}
            <div className="dashboard-details">
              <div className="detail-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0 }}>{t('dashboard.weddingDetails')}</h3>
                  {isOwner && !editingDetails && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-outline" onClick={handleEditDetails} style={{ padding: '6px 12px', fontSize: '13px' }}>
                        <Edit3 size={14} /> {t('common.edit')}
                      </button>
                      <button className="btn btn-outline" onClick={() => setDeleteConfirm(true)} style={{ padding: '6px 12px', fontSize: '13px', color: '#ef4444', borderColor: '#fecaca' }}>
                        <Trash2 size={14} /> {t('common.delete')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Delete Confirmation */}
                {deleteConfirm && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 12px', color: '#991b1b', fontWeight: 600 }}>{t('dashboard.deleteWeddingConfirm')}</p>
                    <p style={{ margin: '0 0 12px', color: '#6b7280', fontSize: '14px' }}>{t('dashboard.deleteWeddingNote')}</p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-primary" onClick={handleDeleteWedding} style={{ background: '#ef4444', borderColor: '#ef4444' }}>{t('dashboard.yesDelete')}</button>
                      <button className="btn btn-outline" onClick={() => setDeleteConfirm(false)}>{t('common.cancel')}</button>
                    </div>
                  </div>
                )}

                {editingDetails ? (
                  <div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('dashboard.partner1Name')}</label>
                        <input type="text" value={editForm.partner1_name} onChange={(e) => setEditForm({ ...editForm, partner1_name: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>{t('dashboard.partner2Name')}</label>
                        <input type="text" value={editForm.partner2_name} onChange={(e) => setEditForm({ ...editForm, partner2_name: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('dashboard.weddingDate')}</label>
                        <input type="date" value={editForm.wedding_date} onChange={(e) => setEditForm({ ...editForm, wedding_date: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>{t('dashboard.venueLocation')}</label>
                        <input type="text" value={editForm.venue_location} onChange={(e) => setEditForm({ ...editForm, venue_location: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-row form-row-3">
                      <div className="form-group">
                        <label>{t('dashboard.city')}</label>
                        <input type="text" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>{t('dashboard.district')}</label>
                        <input type="text" value={editForm.district} onChange={(e) => setEditForm({ ...editForm, district: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>{t('dashboard.province')}</label>
                        <input type="text" value={editForm.province} onChange={(e) => setEditForm({ ...editForm, province: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-row form-row-3">
                      <div className="form-group">
                        <label>{t('dashboard.expectedGuests')}</label>
                        <input type="number" value={editForm.guest_count} onChange={(e) => setEditForm({ ...editForm, guest_count: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>{t('dashboard.budgetAmount')}</label>
                        <input type="number" value={editForm.budget_npr} onChange={(e) => setEditForm({ ...editForm, budget_npr: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>{t('dashboard.currency')}</label>
                        <select value={editForm.budget_currency || 'NPR'} onChange={(e) => setEditForm({ ...editForm, budget_currency: e.target.value })}>
                          <option value="NPR">NPR — Nepali Rupee</option>
                          <option value="USD">USD — US Dollar</option>
                          <option value="INR">INR — Indian Rupee</option>
                          <option value="EUR">EUR — Euro</option>
                          <option value="GBP">GBP — British Pound</option>
                          <option value="AUD">AUD — Australian Dollar</option>
                          <option value="CAD">CAD — Canadian Dollar</option>
                          <option value="JPY">JPY — Japanese Yen</option>
                          <option value="AED">AED — UAE Dirham</option>
                          <option value="QAR">QAR — Qatari Riyal</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('dashboard.weddingStyle')}</label>
                        <select value={editForm.wedding_style} onChange={(e) => setEditForm({ ...editForm, wedding_style: e.target.value })}>
                          <option value="traditional">{t('dashboard.traditional')}</option>
                          <option value="modern">{t('dashboard.modern')}</option>
                          <option value="fusion">{t('dashboard.fusion')}</option>
                          <option value="destination">{t('dashboard.destination')}</option>
                          <option value="intimate">{t('dashboard.intimate')}</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                        <label>{t('dashboard.loveStory')}</label>
                      <textarea value={editForm.story} onChange={(e) => setEditForm({ ...editForm, story: e.target.value })} rows={3} />
                    </div>
                    <div className="form-actions">
                      <button className="btn btn-primary" onClick={handleSaveDetails} disabled={editSaving}>
                        {editSaving ? t('common.saving') : t('vendorPortal.saveChanges')}
                      </button>
                      <button className="btn btn-outline" onClick={() => setEditingDetails(false)}>{t('common.cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <div className="details-list">
                    <div className="detail-row">
                      <Calendar size={16} />
                      <span>{t('dashboard.dateLabel')}</span>
                      <strong>{wedding.wedding_date ? new Date(wedding.wedding_date).toLocaleDateString() : t('common.tbd')}</strong>
                    </div>
                    <div className="detail-row">
                      <MapPin size={16} />
                      <span>{t('dashboard.venueLabel')}</span>
                      <strong>{wedding.venue_location || t('common.tbd')}</strong>
                    </div>
                    <div className="detail-row">
                      <MapPin size={16} />
                      <span>{t('dashboard.cityLabel')}</span>
                      <strong>{[wedding.city, wedding.district].filter(Boolean).join(', ') || t('common.tbd')}</strong>
                    </div>
                    <div className="detail-row">
                      <Users size={16} />
                      <span>{t('dashboard.guestsLabel')}</span>
                      <strong>{wedding.guest_count}</strong>
                    </div>
                    <div className="detail-row">
                      <Wallet size={16} />
                      <span>{t('dashboard.budgetLabel')}</span>
                      <strong>{wedding.budget_currency || 'NPR'} {Number(wedding.budget_npr).toLocaleString()}</strong>
                    </div>
                    <div className="detail-row">
                      <Heart size={16} />
                      <span>{t('dashboard.styleLabel')}</span>
                      <strong className="capitalize">{wedding.wedding_style}</strong>
                    </div>
                  </div>
                )}
              </div>

              {wedding.story && !editingDetails && (
                <div className="detail-card">
                  <h3>{t('dashboard.ourStory')}</h3>
                  <p className="wedding-story">{wedding.story}</p>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="quick-links">
              <h3>{t('dashboard.quickActions')}</h3>
              <div className="quick-links-grid">
                <Link to="/vendors" className="quick-link">{t('dashboard.vendors')}</Link>
                <Link to="/checklist" className="quick-link">{t('dashboard.checklist')}</Link>
                <Link to="/guests" className="quick-link">{t('dashboard.guestList')}</Link>
                <Link to="/budget" className="quick-link">{t('dashboard.budget')}</Link>
                {isOwner && <Link to="/wedding-website" className="quick-link">{t('dashboard.website')}</Link>}
                {isOwner && <Link to="/invitations" className="quick-link">{t('dashboard.invitations')}</Link>}
                <Link to="/media" className="quick-link">{t('dashboard.gallery')}</Link>
                {isOwner && <Link to="/helpers" className="quick-link">{t('dashboard.helpers')}</Link>}
                {isOwner && <Link to="/notifications" className="quick-link">{t('nav.notifications')}</Link>}
              </div>
            </div>



            {/* Bookmarked Vendors */}
            {bookmarks.length > 0 && (
              <div className="detail-card" style={{ marginTop: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}><Heart size={18} style={{ verticalAlign: 'middle', marginRight: '6px', color: '#DC143C' }} />{t('dashboard.bookmarkedVendors')}</h3>
                  <Link to="/vendors" style={{ fontSize: '13px', color: '#DC143C', textDecoration: 'none' }}>{t('dashboard.browseMore')}</Link>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                  {bookmarks.map(bm => (
                    <Link key={bm.vendor_id} to={`/vendor/${bm.vendor_slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="detail-card" style={{ padding: '14px', display: 'flex', gap: '12px', alignItems: 'center', margin: 0 }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: '#f3f4f6' }}>
                          <img src={vendorImage({ cover_photo_url: bm.cover_photo_url, category_slug: bm.category_slug, slug: bm.vendor_slug }, 48, 48)} alt={bm.business_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div>
                          <strong style={{ fontSize: '14px', color: '#1f2937' }}>{bm.business_name}</strong>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{bm.category_name} &middot; {bm.city}</div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;